/**
 * Job Stage Constants for Split Ticket Ordering
 * 
 * Defines the precedence order for job types when splitting tickets.
 * Stage 10 must be scheduled before Stage 20, etc.
 */

export const JOB_STAGES = {
  INSPECTION: 10,    // DVI, diagnostics, multi-point inspection
  MECHANICAL: 20,    // Steering, suspension, brakes, tires, engine, driveline
  ALIGNMENT: 30,     // Alignment work
  ADAS: 40,          // ADAS calibration
  FINAL: 50,         // Road test, QC, detail
} as const;

export type JobStage = typeof JOB_STAGES[keyof typeof JOB_STAGES];

/**
 * Maps job categories to their stage in the ordering
 */
export const CATEGORY_TO_STAGE: Record<string, number> = {
  // Stage 10 - Inspection/Diagnostics
  'inspections': JOB_STAGES.INSPECTION,
  'inspection': JOB_STAGES.INSPECTION,
  'diagnostics': JOB_STAGES.INSPECTION,
  'diagnostic': JOB_STAGES.INSPECTION,
  'dvi': JOB_STAGES.INSPECTION,
  'multi-point': JOB_STAGES.INSPECTION,
  'multipoint': JOB_STAGES.INSPECTION,
  
  // Stage 20 - Mechanical Work
  'engine_mechanical': JOB_STAGES.MECHANICAL,
  'engine_performance': JOB_STAGES.MECHANICAL,
  'steering_suspension': JOB_STAGES.MECHANICAL,
  'steering': JOB_STAGES.MECHANICAL,
  'suspension': JOB_STAGES.MECHANICAL,
  'brakes': JOB_STAGES.MECHANICAL,
  'brake': JOB_STAGES.MECHANICAL,
  'tires': JOB_STAGES.MECHANICAL,
  'tire': JOB_STAGES.MECHANICAL,
  'drivetrain': JOB_STAGES.MECHANICAL,
  'driveline': JOB_STAGES.MECHANICAL,
  'transmission': JOB_STAGES.MECHANICAL,
  'exhaust': JOB_STAGES.MECHANICAL,
  'cooling': JOB_STAGES.MECHANICAL,
  'hvac': JOB_STAGES.MECHANICAL,
  'electrical': JOB_STAGES.MECHANICAL,
  'starting_charging': JOB_STAGES.MECHANICAL,
  'engine': JOB_STAGES.MECHANICAL,
  'ev_hybrid': JOB_STAGES.MECHANICAL,
  'body_frame': JOB_STAGES.MECHANICAL,
  'restraints': JOB_STAGES.MECHANICAL,
  'maintenance': JOB_STAGES.MECHANICAL,
  
  // Stage 30 - Alignment
  'alignment': JOB_STAGES.ALIGNMENT,
  
  // Stage 40 - ADAS
  'adas': JOB_STAGES.ADAS,
  'adas_calibration': JOB_STAGES.ADAS,
  
  // Stage 50 - Final/QC
  'road_test': JOB_STAGES.FINAL,
  'roadtest': JOB_STAGES.FINAL,
  'final_qc': JOB_STAGES.FINAL,
  'qc': JOB_STAGES.FINAL,
  'detail': JOB_STAGES.FINAL,
  'wash': JOB_STAGES.FINAL,
};

/**
 * Get the stage number for a job category
 */
export function getJobStage(categoryKey: string): number {
  const normalizedCategory = categoryKey.toLowerCase().replace(/[^a-z0-9_]/g, '_');
  
  // Direct match
  if (CATEGORY_TO_STAGE[normalizedCategory]) {
    return CATEGORY_TO_STAGE[normalizedCategory];
  }
  
  // Partial match - check if category contains known keywords
  for (const [key, stage] of Object.entries(CATEGORY_TO_STAGE)) {
    if (normalizedCategory.includes(key) || key.includes(normalizedCategory)) {
      return stage;
    }
  }
  
  // Default to MECHANICAL if unknown
  return JOB_STAGES.MECHANICAL;
}

/**
 * Get the maximum (latest required) stage for a set of jobs
 */
export function getTicketMaxStage(jobs: Array<{ category?: string; categoryKey?: string }>): number {
  if (!jobs || jobs.length === 0) return JOB_STAGES.MECHANICAL;
  
  return Math.max(
    ...jobs.map(job => getJobStage(job.category || job.categoryKey || 'mechanical'))
  );
}

/**
 * Get the minimum (earliest required) stage for a set of jobs
 */
export function getTicketMinStage(jobs: Array<{ category?: string; categoryKey?: string }>): number {
  if (!jobs || jobs.length === 0) return JOB_STAGES.MECHANICAL;
  
  return Math.min(
    ...jobs.map(job => getJobStage(job.category || job.categoryKey || 'mechanical'))
  );
}

/**
 * Stage display names for UI
 */
export const STAGE_NAMES: Record<number, string> = {
  [JOB_STAGES.INSPECTION]: 'Inspection/Diagnostics',
  [JOB_STAGES.MECHANICAL]: 'Mechanical Work',
  [JOB_STAGES.ALIGNMENT]: 'Alignment',
  [JOB_STAGES.ADAS]: 'ADAS Calibration',
  [JOB_STAGES.FINAL]: 'Final/QC',
};

/**
 * Get human-readable stage name
 */
export function getStageName(stage: number): string {
  return STAGE_NAMES[stage] || 'Work';
}

/**
 * Validate that ticket chunks are in proper stage order
 * Returns validation result with any warnings
 */
export interface StageValidationResult {
  isValid: boolean;
  warnings: string[];
  orderedChunks: Array<{
    techId: string;
    techName: string;
    jobs: Array<{ id: string; name: string; category: string }>;
    stage: number;
    stageName: string;
  }>;
}

export function validateStageOrdering(
  chunks: Array<{
    techId: string;
    techName: string;
    jobs: Array<{ id: string; name: string; category: string }>;
  }>
): StageValidationResult {
  const warnings: string[] = [];
  
  // Calculate stage for each chunk
  const chunksWithStage = chunks.map(chunk => ({
    ...chunk,
    stage: getTicketMaxStage(chunk.jobs),
    stageName: getStageName(getTicketMaxStage(chunk.jobs)),
  }));
  
  // Sort by stage for proper ordering
  const orderedChunks = [...chunksWithStage].sort((a, b) => a.stage - b.stage);
  
  // Check if original order matches required stage order
  for (let i = 0; i < chunksWithStage.length - 1; i++) {
    const current = chunksWithStage[i];
    const next = chunksWithStage[i + 1];
    
    if (current.stage > next.stage) {
      warnings.push(
        `${current.stageName} (${current.techName}) must be scheduled after ${next.stageName} (${next.techName})`
      );
    }
  }
  
  return {
    isValid: warnings.length === 0,
    warnings,
    orderedChunks,
  };
}
